import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { Loader2, Save } from "lucide-react";

import { supabase } from "../providers/supabase/supabase";
import type { OwnerAssignments, Sale } from "../types";

type Channel = { id: number; name: string };

export const OwnerMappingConfig = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [assignments, setAssignments] = useState<OwnerAssignments>({});
  const [dirty, setDirty] = useState(false);

  const { data: channels } = useQuery({
    queryKey: ["channels"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("channels")
        .select("id, name")
        .order("sort_order");
      if (error) throw error;
      return data as Channel[];
    },
  });

  const { data: salesUsers } = useQuery({
    queryKey: ["sales", "list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sales")
        .select("id, first_name, last_name")
        .eq("disabled", false)
        .order("first_name");
      if (error) throw error;
      return data as Pick<Sale, "id" | "first_name" | "last_name">[];
    },
  });

  const { data: configData } = useQuery({
    queryKey: ["batch_config"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("batch", {
        body: { action: "config" },
      });
      if (error) throw error;
      return data.config as Record<string, unknown>;
    },
  });

  useEffect(() => {
    if (configData?.owner_assignments) {
      setAssignments(configData.owner_assignments as OwnerAssignments);
    }
  }, [configData]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.functions.invoke("batch", {
        body: {
          action: "config",
          config: { ...configData, owner_assignments: assignments },
        },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      notify("Owner assignments saved", { type: "success" });
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ["batch_config"] });
    },
    onError: () => {
      notify("Failed to save owner assignments", { type: "error" });
    },
  });

  const handleChange = (channelName: string, salesId: string) => {
    setAssignments((prev) => ({
      ...prev,
      [channelName]: salesId ? Number(salesId) : null,
    }));
    setDirty(true);
  };

  if (!salesUsers || !channels) return null;

  return (
    <div className="rounded-lg border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Deal Owner Assignments</h3>
        {dirty && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="cursor-pointer"
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Save className="w-3 h-3 mr-1" />
            )}
            Save
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Map sales channels to sales users. Outreach deals will be
        assigned to the selected user after enrichment.
      </p>
      <div className="space-y-2">
        {channels.map((ch) => (
          <div key={ch.id} className="flex items-center gap-3">
            <label className="text-sm w-36 shrink-0">{ch.name}</label>
            <select
              className="h-8 flex-1 rounded-md border bg-background px-2
                text-sm"
              value={assignments[ch.name] ?? ""}
              onChange={(e) => handleChange(ch.name, e.target.value)}
            >
              <option value="">Unassigned</option>
              {salesUsers.map((user) => (
                <option key={String(user.id)} value={String(user.id)}>
                  {user.first_name} {user.last_name}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
};
